import { omit } from "lodash";

import { baseApi } from "../api";
import {
  AccountDetails,
  Accounts,
  AccountsWithDetails,
  BalanceHistory,
  CashbackAdjustment,
  DeleteCashbackAdjustmentArgs,
  DeletionImpact,
  PayloadAccountDetailsMin,
} from "./types";

interface GetAccountsSummaryArgs {
  year?: number;
  activeOnly?: boolean;
}

interface GetAccountBalanceHistoryArgs {
  accountId: number;
  year?: number;
}

interface UpdateAccountArgs {
  accountId: number;
  payload: PayloadAccountDetailsMin;
}

interface UpdateAccountStatusArgs {
  accountId: number;
  isActive: boolean;
}

/**
 * List provides a `LIST` sentinel plus one tag per row, mirroring
 * `investmentsListTags`. A mutation on one account then refreshes the list and
 * that account's detail query without touching the other accounts' details.
 */
const accountsListTags = (result?: Accounts[]) => [
  { type: "Account" as const, id: "LIST" },
  ...(result?.map((account) => ({
    type: "Account" as const,
    id: account.id,
  })) ?? []),
];

/**
 * Aggregates every account write refreshes. `Finances` is here because a new,
 * archived or deleted account changes net worth. The goal tags are here because
 * a linked account's initial balance or status change moves goal progress, which
 * is computed server-side from account balances.
 */
const accountAggregates = [
  "AccountSummary" as const,
  "AccountYears" as const,
  "SavingsGoal" as const,
  "InvestmentGoal" as const,
  "Finances" as const,
];

/**
 * Accounts RTK Query endpoints. This is the reference slice for PMA-222 — other
 * domains follow the same shape: inject queries/mutations onto `baseApi`, tag
 * queries with `providesTags`, and invalidate those tags from mutations so the
 * cache refetches automatically (no hand-rolled `refetch` wiring).
 */
export const accountsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAccountsSummary: builder.query<
      AccountsWithDetails,
      GetAccountsSummaryArgs
    >({
      query: ({ year, activeOnly = true }) => ({
        url: "/accounts/summary",
        params: year === undefined ? { activeOnly } : { activeOnly, year },
      }),
      providesTags: ["AccountSummary"],
    }),

    getAccounts: builder.query<Accounts[], void>({
      query: () => ({ url: "/accounts" }),
      providesTags: accountsListTags,
    }),

    getAccount: builder.query<AccountDetails, number>({
      query: (accountId) => ({ url: "/accounts/" + accountId }),
      providesTags: (_result, _error, accountId) => [
        { type: "Account", id: accountId },
      ],
    }),

    getAccountBalanceHistory: builder.query<
      BalanceHistory,
      GetAccountBalanceHistoryArgs
    >({
      query: ({ accountId, year }) => ({
        url: "/accounts/" + accountId + "/balance-history",
        params: year === undefined ? undefined : { year },
      }),
      providesTags: [{ type: "AccountBalanceHistory", id: "LIST" }],
    }),

    getAccountDeletionImpact: builder.query<DeletionImpact, number>({
      query: (accountId) => ({
        url: "/accounts/" + accountId + "/deletion-impact",
      }),
      providesTags: (_result, _error, accountId) => [
        { type: "Account", id: accountId },
      ],
    }),

    getAccountsYears: builder.query<number[], number | void>({
      query: (accountId) => ({
        url: "/accounts/years",
        params: accountId === undefined ? undefined : { accountId },
      }),
      providesTags: ["AccountYears"],
    }),

    getCashbackAdjustments: builder.query<CashbackAdjustment[], number>({
      query: (accountId) => ({
        url: `/accounts/${accountId}/cashback-adjustments`,
      }),
      providesTags: (_result, _error, accountId) => [
        { type: "Cashback", id: accountId },
      ],
    }),

    deleteCashbackAdjustment: builder.mutation<
      void,
      DeleteCashbackAdjustmentArgs
    >({
      query: ({ accountId, adjustmentId }) => ({
        url: `/accounts/${accountId}/cashback-adjustments/${adjustmentId}`,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: "Cashback", id: accountId },
        { type: "Account", id: accountId },
      ],
    }),

    createAccount: builder.mutation<void, PayloadAccountDetailsMin>({
      query: (payload) => ({
        url: "/accounts",
        method: "post",
        data: omit(payload, "id"),
      }),
      invalidatesTags: [{ type: "Account", id: "LIST" }, ...accountAggregates],
    }),

    updateAccount: builder.mutation<void, UpdateAccountArgs>({
      query: ({ accountId, payload }) => ({
        url: "/accounts/" + accountId,
        method: "patch",
        data: omit(payload, "id"),
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: "Account", id: accountId },
        { type: "Account", id: "LIST" },
        ...accountAggregates,
      ],
    }),

    updateAccountStatus: builder.mutation<void, UpdateAccountStatusArgs>({
      query: ({ accountId, isActive }) => ({
        url: "/accounts/" + accountId,
        method: "patch",
        data: { isActive },
      }),
      /**
       * Optimistic toggle: patch the *detail* query only, never the list.
       * Flipping `isActive` changes list membership, so on an `activeOnly` list
       * the row should disappear rather than sit there flipped — a boolean patch
       * would render a lie. `invalidatesTags` refetches the list with correct
       * membership, and the index page is unmounted while you toggle anyway.
       */
      async onQueryStarted(
        { accountId, isActive },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          accountsApi.util.updateQueryData("getAccount", accountId, (draft) => {
            draft.isActive = isActive;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: "Account", id: accountId },
        { type: "Account", id: "LIST" },
        ...accountAggregates,
      ],
    }),

    deleteAccount: builder.mutation<void, number>({
      query: (accountId) => ({
        url: "/accounts/" + accountId,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, accountId) => [
        { type: "Account", id: accountId },
        { type: "Account", id: "LIST" },
        ...accountAggregates,
      ],
    }),
  }),
});

export const {
  useGetAccountsSummaryQuery,
  useGetAccountsQuery,
  useLazyGetAccountsQuery,
  useGetAccountQuery,
  useGetAccountBalanceHistoryQuery,
  useGetAccountDeletionImpactQuery,
  useLazyGetAccountDeletionImpactQuery,
  useGetAccountsYearsQuery,
  useGetCashbackAdjustmentsQuery,
  useDeleteCashbackAdjustmentMutation,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useUpdateAccountStatusMutation,
  useDeleteAccountMutation,
} = accountsApi;
